import React, { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useContext, useEffect, useState, useRef } from "react";
import { UserContext } from "../contexts/UserContext";
import NotificationPop from "./NotificationPop";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { DeliveryRequestsContext } from "../contexts/DeliveryRequestsContext";
import { MdGroups } from "react-icons/md";
import { FaImages } from "react-icons/fa";
import { toast } from "react-toastify";
import { NotificationContext } from "../contexts/NotificationContext";
import { BeatLoader } from "react-spinners";

// Inline SVG sailboat icon to avoid importing the 14 MB react-icons/gi package
const SailboatIcon = ({ size = 24, ...props }) => (
  <svg viewBox="0 0 512 512" width={size} height={size} fill="currentColor" {...props}>
    <path d="M199.256 74.5v285H27.744l25.998 78H380.255l104-78h-267v-285h-18zm18 18c36.787 88.85 64.94 216 0 250h208c22-34-11.905-164.76-208-250zm-36 0c-33.046 69.333-50 200-144 250h144v-250z" />
  </svg>
);

function MobileFooter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { t } = useTranslation();
  const [showCreatePostModal, setShowCreatePostModal] = React.useState(false);
  const [showNotifPop, setShowNotifPop] = useState(false);
  const [notifTarget, setNotifTarget] = useState(null);
  const bellRef = useRef(null);
  const { unreadCount, setUnreadCount, liveNotification, setLiveNotification, fetchUnreadCount } = useContext(NotificationContext);
  const [showFallbackModal, setShowFallbackModal] = useState(false);
  const [notificationsFallback, setNotificationsFallback] = useState([]);
  const [loadingFallback, setLoadingFallback] = useState(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const { showQuotes } = useContext(DeliveryRequestsContext);
  const [fallbackIsRead, setFallbackIsRead] = useState(false);
  const [fallbackPage, setFallbackPage] = useState(1);
  const [fallbackHasMore, setFallbackHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const modalBodyRef = useRef(null);

  // Active tab color
  const activeColor = "#0c5272";
  const inactiveColor = "#6c757d"; // text-secondary equivalent

  // Helper function to check if a route is active
  const isActiveRoute = (routes) => {
    if (Array.isArray(routes)) {
      return routes.some((route) => location.pathname === route);
    }
    return location.pathname === routes;
  };

  // Helper function to get tab style
  const getTabStyle = (routes) => {
    return {
      color: isActiveRoute(routes) ? activeColor : inactiveColor,
      cursor: "pointer",
    };
  };


  // Fetch notifications for the fallback modal
  const fetchFallbackNotifications = async (isRead = false, page = 1) => {
    if (page === 1) {
      setLoadingFallback(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setNotificationsFallback([]);
        setLoadingFallback(false);
        setLoadingMore(false);
        return;
      }
      const res = await fetch(
        `${apiUrl}/user/notification/get?is_read=${isRead ? 1 : 0}&page=${page}&per_page=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        },
      );
      if (!res.ok) {
        if (page === 1) setNotificationsFallback([]);
        setLoadingFallback(false);
        setLoadingMore(false);
        setFallbackHasMore(false);
        return;
      }
      const data = await res.json();
      if (data && data.status && Array.isArray(data.data)) {
        const parseNotif = (item) => {
          if (!item) return {};
          let n = { ...item };
          if (n.data) {
            try {
              const parsed =
                typeof n.data === "string" ? JSON.parse(n.data) : n.data;
              n = { ...n, ...parsed };
            } catch (err) {
              // ignore parse errors
            }
          }
          if (!n.sender_id) {
            const senderId =
              n.sender_id ||
              n.senderId ||
              (n.sender && (n.sender.id || n.sender_id)) ||
              n.sender?.id;
            if (senderId) n.sender_id = senderId;
          }
          return n;
        };
        const parsed = data.data.map(parseNotif);
        if (page === 1) {
          setNotificationsFallback(parsed);
        } else {
          setNotificationsFallback((prev) => [...prev, ...parsed]);
        }
        setFallbackPage(data.meta?.current_page ?? page);
        setFallbackHasMore((data.meta?.current_page ?? page) < (data.meta?.last_page ?? 1));
      } else {
        if (page === 1) setNotificationsFallback([]);
        setFallbackHasMore(false);
      }
    } catch (e) {
      if (page === 1) setNotificationsFallback([]);
      setFallbackHasMore(false);
    }
    setLoadingFallback(false);
    setLoadingMore(false);
  };

  const markAsReadFallback = async (notifId) => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${apiUrl}/user/notification/mark-read/${notifId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      setNotificationsFallback((prev) =>
        prev.map((n) => (n.id === notifId ? { ...n, is_read: true } : n)),
      );
      // Immediately decrement, then sync with server
      setUnreadCount((prev) => Math.max(0, prev - 1));
      fetchUnreadCount();
    } catch (e) {
      // ignore
    }
  };

  const handleModalScroll = useCallback(() => {
    const el = modalBodyRef.current;
    if (!el || loadingMore || !fallbackHasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
      fetchFallbackNotifications(fallbackIsRead, fallbackPage + 1);
    }
  }, [loadingMore, fallbackHasMore, fallbackIsRead, fallbackPage]);

  // Load fallback notifications when the modal is opened
  useEffect(() => {
    if (showFallbackModal) {
      setFallbackIsRead(false);
      setFallbackPage(1);
      setFallbackHasMore(false);
      fetchFallbackNotifications(false, 1);
      // Count stays until notifications are actually marked as read
    }
  }, [showFallbackModal]);

  // Detect keyboard open on iOS Safari
  useEffect(() => {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isMobile = isIOS || isAndroid;

    if (!isMobile) return;

    let initialViewportHeight = window.innerHeight;
    let focusTimeout = null;

    const isInputElement = (el) => {
      if (!el) return false;
      const tagName = el.tagName?.toUpperCase();
      return (
        tagName === "INPUT" ||
        tagName === "TEXTAREA" ||
        tagName === "SELECT" ||
        el.isContentEditable === true ||
        el.contentEditable === "true"
      );
    };

    const handleFocusIn = (e) => {
      if (isInputElement(e.target)) {
        if (focusTimeout) {
          clearTimeout(focusTimeout);
          focusTimeout = null;
        }
        setIsKeyboardOpen(true);
      }
    };

    const handleFocusOut = (e) => {
      if (isInputElement(e.target)) {
        focusTimeout = setTimeout(() => {
          const activeEl = document.activeElement;
          if (!isInputElement(activeEl)) {
            setIsKeyboardOpen(false);
          }
        }, 150);
      }
    };

    const handleViewportResize = () => {
      if (window.visualViewport) {
        const currentHeight = window.visualViewport.height;
        const heightDiff = initialViewportHeight - currentHeight;

        if (heightDiff > 100) {
          setIsKeyboardOpen(true);
        } else if (heightDiff < 50) {
          const activeEl = document.activeElement;
          if (!isInputElement(activeEl)) {
            setIsKeyboardOpen(false);
          }
        }
      }
    };

    const handleScroll = () => {
      if (window.visualViewport) {
        const currentHeight = window.visualViewport.height;
        const heightDiff = initialViewportHeight - currentHeight;
        if (heightDiff > 100) {
          setIsKeyboardOpen(true);
        }
      }
    };

    document.addEventListener("focusin", handleFocusIn, true);
    document.addEventListener("focusout", handleFocusOut, true);

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleViewportResize);
      window.visualViewport.addEventListener("scroll", handleScroll);
    }

    const handleResize = () => {
      if (!isInputElement(document.activeElement)) {
        initialViewportHeight = window.innerHeight;
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      if (focusTimeout) clearTimeout(focusTimeout);
      document.removeEventListener("focusin", handleFocusIn, true);
      document.removeEventListener("focusout", handleFocusOut, true);
      window.removeEventListener("resize", handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          handleViewportResize,
        );
        window.visualViewport.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  // Inject CSS styles for keyboard-open state
  useEffect(() => {
    const styleId = "mobile-footer-keyboard-styles";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        body.keyboard-open .mobile-footer,
        body.keyboard-open [class*="mobile-footer"],
        body.keyboard-open [class*="MobileFooter"] {
          display: none !important;
          visibility: hidden !important;
          height: 0 !important;
          max-height: 0 !important;
          overflow: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
          position: absolute !important;
          top: -9999px !important;
          left: -9999px !important;
        }
      `;
      document.head.appendChild(style);
    }
    return () => {};
  }, []);

  // Add/remove body class when keyboard opens/closes
  useEffect(() => {
    if (isKeyboardOpen) {
      document.body.classList.add("keyboard-open");
    } else {
      document.body.classList.remove("keyboard-open");
    }
    return () => {
      document.body.classList.remove("keyboard-open");
    };
  }, [isKeyboardOpen]);

  const hideFooterRoutes = [
    "/login",
    "/signup",
    "/privacy-policy",
    "/terms-and-conditions",
    "/cookie-policy",
    "/account-deletion-guide",
    "/contact-support",
  ];
  const showFooter = !hideFooterRoutes.includes(location.pathname);
  const isRole2 = user?.role === 2;
  const isRole3or4 = user?.role === 3 || user?.role === 4;

  const handlePostClick = () => {
    navigate("/portfolio");
    setShowCreatePostModal(true);
  };

  return (
    <>
      {showFooter && (
        <>
          <div
            className="mobile-footer bg-white border-top d-flex justify-content-around align-items-center py-2 shadow-sm"
            style={
              isKeyboardOpen
                ? {
                    position: "fixed",
                    bottom: "-100px",
                    opacity: 0,
                    pointerEvents: "none",
                    visibility: "hidden",
                  }
                : {}
            }
          >
            {/* Home */}
            <div
              className="d-flex flex-column align-items-center small"
              style={getTabStyle("/portfolio")}
              onClick={() => navigate("/portfolio")}
            >
              <i className="bi bi-house-fill fs-3"></i>
              <span>{t("mobileFooter.home", { defaultValue: "Home" })}</span>
            </div>

            {/* Groups */}
            <div
              className="d-flex flex-column align-items-center small"
              style={getTabStyle("/Groups")}
              onClick={() => navigate("/Groups")}
            >
              <MdGroups size={33} />
              <span>
                {t("mobileFooter.groups", { defaultValue: "Groups" })}
              </span>
            </div>

            {/* Media */}
            <div
              className="d-flex flex-column align-items-center small"
              style={getTabStyle("/media-library")}
              onClick={() => navigate("/media-library")}
            >
              <FaImages size={33} />
              <span>Media</span>
            </div>

            {/* My Vessel / Add Vessel (Role 2 only) */}
            {(isRole2 || isRole3or4) && (
              <div
                className="d-flex flex-column align-items-center small"
                style={getTabStyle(["/my-vessel", "/add-vessel"])}
                onClick={() =>
                  user?.vesselsCount > 0
                    ? navigate("/my-vessel")
                    : navigate("/add-vessel")
                }
              >
                {user?.vesselsCount > 0 ? (
                  <SailboatIcon size={33} />
                ) : (
                  <i className="bi bi-plus-square-fill fs-3"></i>
                )}
                <span>
                  {user?.vesselsCount > 0 ? "My vessels" : "Add vessel"}
                </span>
              </div>
            )}

            {/* Mates */}
            <div
              className="d-flex flex-column align-items-center small"
              style={getTabStyle("/mates")}
              onClick={() => navigate("/mates")}
            >
              <i className="bi bi-people-fill fs-3"></i>
              <span>{t("leftPanel.nav.mates", { defaultValue: "Mates" })}</span>
            </div>

            {/* Quotes (Role 2 with showQuotes) */}
            {showQuotes && isRole2 && (
              <div
                className="d-flex flex-column align-items-center small"
                style={getTabStyle("/delivery-quotes")}
                onClick={() => navigate("/delivery-quotes")}
              >
                <i className="bi bi-briefcase-fill fs-3"></i>
                <span>
                  {t("mobileFooter.quotes", { defaultValue: "Quotes" })}
                </span>
              </div>
            )}

            {/* Jobs (Role 3/4) */}
            {isRole3or4 && (
              <div
                className="d-flex flex-column align-items-center small"
                style={getTabStyle("/delivery-quotes")}
                onClick={() => navigate("/delivery-quotes")}
              >
                <i className="bi bi-briefcase-fill fs-3"></i>
                <span>Jobs</span>
              </div>
            )}

            {/* Alert (Role 2, 3, 4) */}
            {(isRole2 || isRole3or4) && (
              <div
                ref={bellRef}
                className="d-flex flex-column align-items-center small position-relative"
                style={{
                  color: showFallbackModal ? activeColor : inactiveColor,
                  cursor: "pointer",
                }}
                onClick={() => {
                  setShowFallbackModal(true);
                }}
              >
                <i className="bi bi-bell-fill fs-3"></i>
                {unreadCount > 0 && (
                  <span
                    className="position-absolute top-0 start-50 translate-middle badge rounded-pill bg-danger"
                    style={{ fontSize: "0.6rem" }}
                  >
                    {unreadCount}
                  </span>
                )}
                <span>
                  {t("mobileFooter.alert", { defaultValue: "Alert" })}
                </span>
              </div>
            )}
          </div>

          {/* Notification popover */}
          <NotificationPop
            show={showNotifPop}
            target={notifTarget}
            setShow={setShowNotifPop}
            liveNotification={liveNotification}
            setLiveNotification={setLiveNotification}
          />

          {/* Fallback modal */}
          <Modal
            show={showFallbackModal}
            onHide={() => setShowFallbackModal(false)}
            centered
            scrollable
            backdrop="static"
            onShow={() => { document.body.style.overflow = "hidden"; }}
            onExited={() => { document.body.style.overflow = ""; }}
          >
            <Modal.Header closeButton>
              <div className="d-flex justify-content-between align-items-center w-100 me-2">
                <Modal.Title>
                  {t("mobileFooter.notifications", {
                    defaultValue: "Notifications",
                  })}
                </Modal.Title>
                <div className="form-check form-switch mb-0">
                  <input
                    className="form-check-input custom-toggle"
                    type="checkbox"
                    id="fallbackNotifToggle"
                    checked={fallbackIsRead}
                    style={{ fontSize: 17 }}
                    onChange={(e) => {
                      setFallbackIsRead(e.target.checked);
                      setFallbackPage(1);
                      setFallbackHasMore(false);
                      fetchFallbackNotifications(e.target.checked, 1);
                    }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="fallbackNotifToggle"
                  >
                    {fallbackIsRead
                      ? t("mobileFooter.read", { defaultValue: "Read" })
                      : t("mobileFooter.unread", { defaultValue: "Unread" })}
                  </label>
                </div>
              </div>
            </Modal.Header>
            <Modal.Body ref={modalBodyRef} onScroll={handleModalScroll} style={{ maxHeight: "60vh", overflowY: "auto" }}>
              {loadingFallback ? (
                <div className="text-center py-3">
                  {t("common.loading", { defaultValue: "Loading..." })}
                </div>
              ) : notificationsFallback && notificationsFallback.length > 0 ? (
                notificationsFallback.map((notif) => (
                  <div className="mb-3" key={notif.id}>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <p className="mb-1 fw-bold">
                          {notif.title ||
                            t("mobileFooter.notification_default", {
                              defaultValue: "Notification",
                            })}
                        </p>
                        <p className="mb-1 text-muted small">{notif.message}</p>
                        <div className="d-flex gap-2 mt-2">
                          {(notif.link || notif.type === "friend_request") && (
                            <button
                              className="btn btn-blue btn-sm"
                              onClick={() => {
                                setShowFallbackModal(false);
                                if (notif.type === "friend_request") {
                                  const senderId =
                                    notif.sender_id ||
                                    notif.senderId ||
                                    (notif.sender &&
                                      (notif.sender.id || notif.sender_id)) ||
                                    notif.sender?.id;
                                  navigate("/personal-information", {
                                    state: { personId: senderId },
                                  });
                                  return;
                                }

                                if (
                                  notif.type === "group_event" &&
                                  notif.link
                                ) {
                                  console.log(
                                    notif,
                                    " navigating to group post",
                                  );
                                  const groupId =
                                    notif.sender_id ||
                                    notif.senderId ||
                                    (notif.sender &&
                                      (notif.sender.id || notif.sender_id)) ||
                                    notif.sender?.id;
                                  try {
                                    const urlObj = new URL(notif.link);
                                    const isSameOrigin =
                                      urlObj.origin === window.location.origin;
                                    const path = `${urlObj.pathname || ""}${urlObj.search || ""}${urlObj.hash || ""}`;
                                    if (isSameOrigin) {
                                      navigate(path, { state: { groupId } });
                                    } else {
                                      const sep = notif.link.includes("?")
                                        ? "&"
                                        : "?";
                                      window.location.href = `${notif.link}${sep}groupId=${encodeURIComponent(groupId)}`;
                                    }
                                  } catch (err) {
                                    navigate(notif.link, {
                                      state: { groupId },
                                    });
                                  }
                                  return;
                                }

                                if (
                                  notif.link &&
                                  /^https?:\/\//.test(notif.link)
                                ) {
                                  window.location.href = notif.link;
                                } else if (notif.link) {
                                  navigate(notif.link);
                                }
                              }}
                            >
                              {t("mobileFooter.view_details", {
                                defaultValue: "View Details",
                              })}
                            </button>
                          )}
                          {!notif.is_read && (
                            <button
                              className="btn btn-outline-success btn-sm"
                              onClick={async () => {
                                await markAsReadFallback(notif.id);
                              }}
                            >
                              {t("mobileFooter.mark_as_read", {
                                defaultValue: "Mark as Read",
                              })}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="text-end small text-muted">
                        {notif.hour_ago}
                      </div>
                    </div>
                    <hr />
                  </div>
                ))
              ) : (
                <div className="text-center text-muted">
                  {t("mobileFooter.no_notifications", {
                    defaultValue: "No notifications found.",
                  })}
                </div>
              )}
              {loadingMore && (
                <div className="text-center py-3">
                  <BeatLoader color="#0a425c" size={10} />
                </div>
              )}
            </Modal.Body>
          </Modal>

          {showCreatePostModal && (
            <div style={{ position: "fixed", zIndex: 9999, inset: 0 }}>
              {(() => {
                const CreatePostModal = React.lazy(
                  () => import("../Posts/CreatePostModal"),
                );
                return (
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <CreatePostModal
                      onClose={() => setShowCreatePostModal(false)}
                      url={process.env.REACT_APP_LARAVEL_DOMAIN_URL}
                      ispublic={user?.is_public}
                      onPostSuccess={() => setShowCreatePostModal(false)}
                      onPostError={() => setShowCreatePostModal(false)}
                    />
                  </React.Suspense>
                );
              })()}
            </div>
          )}
        </>
      )}
    </>
  );
}

export default MobileFooter;

// import React from "react";
// import { useLocation, useNavigate } from "react-router-dom";
// import { useContext, useEffect, useState, useRef } from "react";
// import { UserContext } from "../contexts/UserContext";
// import NotificationPop from './NotificationPop';
// import Modal from 'react-bootstrap/Modal';
// import { useTranslation } from 'react-i18next';
// import { DeliveryRequestsContext } from "../contexts/DeliveryRequestsContext";
// import { MdGroups } from "react-icons/md";
// import { FaImages } from "react-icons/fa";
// import { SailboatIcon } from "react-icons/gi";
// import { toast } from "react-toastify";

// function MobileFooter() {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const { user } = useContext(UserContext);
//   const { t } = useTranslation();
//   const [showCreatePostModal, setShowCreatePostModal] = React.useState(false);
//   const [showNotifPop, setShowNotifPop] = useState(false);
//   const [notifTarget, setNotifTarget] = useState(null);
//   const bellRef = useRef(null);
//   const [unreadCount, setUnreadCount] = useState(0);
//   const [serverUnread, setServerUnread] = useState(0); // last server-synced unread count
//   const [localDelta, setLocalDelta] = useState(0); // increments since last server sync
//   const [liveNotification, setLiveNotification] = useState(null);
//   const [showFallbackModal, setShowFallbackModal] = useState(false);
//   const [notificationsFallback, setNotificationsFallback] = useState([]);
//   const [loadingFallback, setLoadingFallback] = useState(false);
//   const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
//   const apiUrl = process.env.REACT_APP_API_URL;
//   const DEBUG_MOBILE_FOOTER = true;
//   const { showQuotes } = useContext(DeliveryRequestsContext);
//   const fetchRequestIdRef = useRef(0);
//   console.log("1",unreadCount);
//   // Fetch unread notifications count for badge
//   const fetchUnreadCount = async () => {
//     if (DEBUG_MOBILE_FOOTER) console.debug('[MobileFooter] fetchUnreadCount start', { serverUnread, localDelta, unreadCount });
//     const reqId = ++fetchRequestIdRef.current;
//     try {
//       const token = localStorage.getItem('token');
//       if (!token) {
//         console.debug('[MobileFooter] no token, unreadCount -> 0');
//         setServerUnread(0);
//         setLocalDelta(0);
//         setUnreadCount(0);
//         return;
//       }
//       const res = await fetch(`${apiUrl}/user/notification/get?is_read=0`, {
//         headers: {
//           Authorization: `Bearer ${token}`,
//           Accept: 'application/json'
//         }
//       });
//       console.debug('[MobileFooter] fetchUnreadCount status:', res.status);
//       if (!res.ok) {
//         console.debug('[MobileFooter] fetchUnreadCount returned not ok');
//         setUnreadCount(0);
//         return;
//       }
//       const data = await res.json();
//       console.debug('[MobileFooter] fetchUnreadCount payload:', data);
//       if (data && data.status && Array.isArray(data.data)) {
//         const serverCount = data.data.length;
//         if (reqId !== fetchRequestIdRef.current) {
//           if (DEBUG_MOBILE_FOOTER) console.debug('[MobileFooter] fetchUnreadCount ignoring stale response', { reqId, current: fetchRequestIdRef.current });
//           return;
//         }
//         if (DEBUG_MOBILE_FOOTER) console.debug('[MobileFooter] fetchUnreadCount serverCount', { serverCount });
//         // Prevent sudden decreases in the UI by keeping the displayed count at least
//         // as high as the locally incremented value until server has had time to settle.
//   const prevDisplayed = (serverUnread || 0) + (localDelta || 0);
//         const displayed = Math.max(prevDisplayed, serverCount);
//         if (DEBUG_MOBILE_FOOTER) console.debug('[MobileFooter] fetchUnreadCount compute', { prevDisplayed, displayed, serverUnread, localDelta });
//         setServerUnread(serverCount);
//         // localDelta is the difference between displayed and serverCount
//         setLocalDelta(displayed - serverCount);
//         setUnreadCount(displayed);
//       } else {
//         if (DEBUG_MOBILE_FOOTER) console.debug('[MobileFooter] fetchUnreadCount no data or bad format');
//         setServerUnread(0);
//         setLocalDelta(0);
//         setUnreadCount(0);
//       }
//     } catch (e) {
//       console.debug('[MobileFooter] fetchUnreadCount error:', e);
//       // keep current displayed count on error
//     }
//   };
//    console.log("2",unreadCount);
//   // Fetch notifications for the fallback modal (can show read/unread depending on filter)
//   const fetchFallbackNotifications = async (isRead = false) => {
//     setLoadingFallback(true);
//     try {
//       const token = localStorage.getItem('token');
//       if (!token) {
//         setNotificationsFallback([]);
//         setLoadingFallback(false);
//         return;
//       }
//       const res = await fetch(`${apiUrl}/user/notification/get?is_read=${isRead ? 1 : 0}`, {
//         headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
//       });
//       if (!res.ok) {
//         setNotificationsFallback([]);
//         setLoadingFallback(false);
//         return;
//       }
//       const data = await res.json();
//       if (data && data.status && Array.isArray(data.data)) {
//         // Normalize/parse notification payloads (some APIs return JSON in `data` field)
//         const parseNotif = (item) => {
//           if (!item) return {};
//           let n = { ...item };
//           if (n.data) {
//             try {
//               const parsed = typeof n.data === 'string' ? JSON.parse(n.data) : n.data;
//               n = { ...n, ...parsed };
//             } catch (err) {
//               // ignore parse errors
//             }
//           }
//           // Ensure sender_id is available at top-level if nested
//           if (!n.sender_id) {
//             const senderId = n.sender_id || n.senderId || (n.sender && (n.sender.id || n.sender_id)) || n.sender?.id;
//             if (senderId) n.sender_id = senderId;
//           }
//           return n;
//         };

//         setNotificationsFallback(data.data.map(parseNotif));
//       } else {
//         setNotificationsFallback([]);
//       }
//     } catch (e) {
//       setNotificationsFallback([]);
//     }
//     setLoadingFallback(false);
//   };

//   useEffect(() => {
//     console.log(">>>new",unreadCount);
//   },[unreadCount])

//   const markAsReadFallback = async (notifId) => {
//     try {
//       const token = localStorage.getItem('token');
//       await fetch(`${apiUrl}/user/notification/mark-read/${notifId}`, {
//         method: 'POST',
//         headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
//       });
//       // Optimistically update fallback list and unread badge
//       setNotificationsFallback((prev) => prev.map(n => n.id === notifId ? { ...n, is_read: true } : n));
//       fetchUnreadCount();
//     } catch (e) {
//       // ignore
//     }
//   };

//   useEffect(() => {
//     fetchUnreadCount();
//   }, [user]);

//   // Listen for app-wide live notifications dispatched from Header (or other places)
//   useEffect(() => {
//     const fetchDebounceRef = { current: null };

//     const scheduleFetch = () => {
//       try {
//         if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current);
//       } catch (e) {}
//       fetchDebounceRef.current = setTimeout(() => {
//         fetchUnreadCount();
//         fetchDebounceRef.current = null;
//       }, 1500);
//     };

//     const onLive = (evt) => {
//       const e = evt?.detail || evt;
//       if (DEBUG_MOBILE_FOOTER) console.debug('[MobileFooter] onLive received', { e, serverUnread, localDelta, unreadCount });
//       // Immediately update local delta for snappy UI
//       setLocalDelta((d) => (typeof d === 'number' ? d + 1 : 1));
//       setUnreadCount((prev) => (typeof prev === 'number' ? prev + 1 : 1));
//       setLiveNotification(e);
//      console.log("new notif",e);
//       if (DEBUG_MOBILE_FOOTER) console.debug('[MobileFooter] onLive updated', { serverUnread, localDelta, unreadCount });
//       // Schedule a reconciliation with the server shortly after
//       scheduleFetch();
//     };

//     window.addEventListener('app:live-notification', onLive);

//     // Also detect cross-tab via localStorage marker
//     const onStorage = (ev) => {
//       if (ev.key === 'lastLiveNotification' && ev.newValue) {
//         try {
//           const parsed = JSON.parse(ev.newValue);
//           if (parsed && parsed.data) {
//             if (DEBUG_MOBILE_FOOTER) console.debug('[MobileFooter] storage event parsed', parsed);
//             setLocalDelta((d) => (typeof d === 'number' ? d + 1 : 1));
//             setUnreadCount((prev) => (typeof prev === 'number' ? prev + 1 : 1));
//             setLiveNotification(parsed.data);
//             scheduleFetch();
//           }
//         } catch (err) {}
//       }
//     };
//     console.log(`livnotfic ${liveNotification}`);
//     window.addEventListener('storage', onStorage);

//     return () => {
//       window.removeEventListener('app:live-notification', onLive);
//       window.removeEventListener('storage', onStorage);
//       try { if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current); } catch (e) {}
//     };
//   }, []);

//   // Load fallback notifications when the modal is opened
//   useEffect(() => {
//     if (showFallbackModal) {
//       fetchFallbackNotifications(false);
//     }
//   }, [showFallbackModal]);

//   // Detect keyboard open on iOS Safari by listening for input focus/blur
//   // and visualViewport resize events
//   useEffect(() => {
//     const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
//     const isAndroid = /Android/i.test(navigator.userAgent);
//     const isMobile = isIOS || isAndroid;

//     if (!isMobile) return;

//     let initialViewportHeight = window.innerHeight;
//     let focusTimeout = null;

//     const isInputElement = (el) => {
//       if (!el) return false;
//       const tagName = el.tagName?.toUpperCase();
//       return (
//         tagName === 'INPUT' ||
//         tagName === 'TEXTAREA' ||
//         tagName === 'SELECT' ||
//         el.isContentEditable === true ||
//         el.contentEditable === 'true'
//       );
//     };

//     const handleFocusIn = (e) => {
//       if (isInputElement(e.target)) {
//         // Clear any pending blur timeout
//         if (focusTimeout) {
//           clearTimeout(focusTimeout);
//           focusTimeout = null;
//         }
//         // Immediately hide footer on focus
//         setIsKeyboardOpen(true);
//       }
//     };

//     const handleFocusOut = (e) => {
//       if (isInputElement(e.target)) {
//         // Delay to check if focus moved to another input
//         focusTimeout = setTimeout(() => {
//           const activeEl = document.activeElement;
//           // Only show footer if focus is NOT on another input
//           if (!isInputElement(activeEl)) {
//             setIsKeyboardOpen(false);
//           }
//         }, 150);
//       }
//     };

//     // visualViewport resize - most reliable for iOS Safari
//     const handleViewportResize = () => {
//       if (window.visualViewport) {
//         const currentHeight = window.visualViewport.height;
//         const heightDiff = initialViewportHeight - currentHeight;

//         // Keyboard typically takes 250px+ on mobile
//         if (heightDiff > 100) {
//           setIsKeyboardOpen(true);
//         } else if (heightDiff < 50) {
//           // Only close if we're sure keyboard is gone
//           const activeEl = document.activeElement;
//           if (!isInputElement(activeEl)) {
//             setIsKeyboardOpen(false);
//           }
//         }
//       }
//     };

//     // Also handle scroll events which can indicate keyboard changes on iOS
//     const handleScroll = () => {
//       if (window.visualViewport) {
//         const currentHeight = window.visualViewport.height;
//         const heightDiff = initialViewportHeight - currentHeight;
//         if (heightDiff > 100) {
//           setIsKeyboardOpen(true);
//         }
//       }
//     };

//     document.addEventListener('focusin', handleFocusIn, true);
//     document.addEventListener('focusout', handleFocusOut, true);

//     if (window.visualViewport) {
//       window.visualViewport.addEventListener('resize', handleViewportResize);
//       window.visualViewport.addEventListener('scroll', handleScroll);
//     }

//     // Update initial height on orientation change
//     const handleResize = () => {
//       if (!isInputElement(document.activeElement)) {
//         initialViewportHeight = window.innerHeight;
//       }
//     };
//     window.addEventListener('resize', handleResize);

//     return () => {
//       if (focusTimeout) clearTimeout(focusTimeout);
//       document.removeEventListener('focusin', handleFocusIn, true);
//       document.removeEventListener('focusout', handleFocusOut, true);
//       window.removeEventListener('resize', handleResize);
//       if (window.visualViewport) {
//         window.visualViewport.removeEventListener('resize', handleViewportResize);
//         window.visualViewport.removeEventListener('scroll', handleScroll);
//       }
//     };
//   }, []);

//   // Inject CSS styles for keyboard-open state
//   useEffect(() => {
//     const styleId = 'mobile-footer-keyboard-styles';
//     if (!document.getElementById(styleId)) {
//       const style = document.createElement('style');
//       style.id = styleId;
//       style.textContent = `
//         body.keyboard-open * {
//           // padding-bottom: revert;
//         }
//         body.keyboard-open,
//         body.keyboard-open html,
//         body.keyboard-open #root,
//         body.keyboard-open .App,
//         body.keyboard-open > div,
//         body.keyboard-open .main-content,
//         body.keyboard-open .app-container,
//         body.keyboard-open .page-content,
//         body.keyboard-open .content-wrapper,
//         body.keyboard-open .container,
//         body.keyboard-open .container-fluid,
//         body.keyboard-open main,
//         body.keyboard-open .add-vessel,
//         body.keyboard-open .addVesselRightSection,
//         body.keyboard-open .addVesselOuter,
//         body.keyboard-open .addVesselOuter2,
//         body.keyboard-open .mobile_topbottom_space,
//         body.keyboard-open .addVesselInner1,
//         body.keyboard-open .row,
//         body.keyboard-open form,
//         body.keyboard-open [class*="layout"],
//         body.keyboard-open [class*="wrapper"],
//         body.keyboard-open [class*="content"],
//         body.keyboard-open [class*="Outer"],
//         body.keyboard-open [class*="Section"],
//         body.keyboard-open [class*="footer"],
//         body.keyboard-open [class*="Footer"] {
//           // padding-bottom: 0 !important;
//           // margin-bottom: 0 !important;
//         }
//         body.keyboard-open {
//           // padding-bottom: 0 !important;
//           // margin-bottom: 0 !important;
//           // min-height: auto !important;
//         }
//         body.keyboard-open .mobile-footer,
//         body.keyboard-open [class*="mobile-footer"],
//         body.keyboard-open [class*="MobileFooter"] {
//           display: none !important;
//           visibility: hidden !important;
//           height: 0 !important;
//           max-height: 0 !important;
//           overflow: hidden !important;
//           opacity: 0 !important;
//           pointer-events: none !important;
//           position: absolute !important;
//           top: -9999px !important;
//           left: -9999px !important;
//         }
//       `;
//       document.head.appendChild(style);
//     }
//     return () => {
//       // Don't remove styles on unmount - they're reusable
//     };
//   }, []);

//   // Add/remove body class when keyboard opens/closes
//   useEffect(() => {
//     if (isKeyboardOpen) {
//       document.body.classList.add('keyboard-open');
//     } else {
//       document.body.classList.remove('keyboard-open');
//     }
//     return () => {
//       document.body.classList.remove('keyboard-open');
//     };
//   }, [isKeyboardOpen]);

//   // Only show Home, Post, Alert, Sail board for user.role == 2
//   const hideFooterRoutes = ["/login", "/signup", "/privacy-policy", "/terms-and-conditions", "/cookie-policy", "/account-deletion-guide","/contact-support"];
//   const showFooter = !hideFooterRoutes.includes(location.pathname);
//   const isRole2 = user?.role === 2;
//   const isRole3or4 = user?.role === 3 || user?.role === 4;

//   // Handler for Post button
//   const handlePostClick = () => {
//     navigate("/portfolio");
//     setShowCreatePostModal(true);
//   };

//   return (
//     <>
//       {showFooter && (
//         <>
//           <div
//             className="mobile-footer bg-white border-top d-flex justify-content-around align-items-center py-2 shadow-sm"
//             style={isKeyboardOpen ? {
//               position: 'fixed',
//               bottom: '-100px',
//               opacity: 0,
//               pointerEvents: 'none',
//               visibility: 'hidden'
//             } : {}}
//           >
//             {/* Home always shown */}
//             <div
//               className="d-flex flex-column align-items-center text-secondary small"
//               onClick={() => navigate("/portfolio")}
//             >
//               <i className="bi bi-house-fill fs-3"></i>
//               <span>{t('mobileFooter.home', { defaultValue: 'Home' })}</span>
//             </div>

//             <div
//               className="d-flex flex-column align-items-center text-secondary small"
//               onClick={() => navigate("/Groups")}
//             >
//               {/* <i className="bi bi-people-fill me-2"></i> */}
//               <MdGroups size={33} />
//               <span>{t('mobileFooter.groups', { defaultValue: 'Groups' })}</span>
//             </div>
//             <div
//               className="d-flex flex-column align-items-center text-secondary small"
//               onClick={() => navigate("/media-library")}
//             >
//               {/* <i className="bi bi-people-fill me-2"></i> */}
//               <FaImages size={33}/>
//               <span>Media</span>
//             </div>
//             {isRole2 && (
//               <div
//                              className="d-flex flex-column align-items-center text-secondary small"
//                              onClick={() => user?.vesselsCount > 0 ? navigate("/my-vessel") : navigate("/add-vessel")}
//                            >
//                              {user?.vesselsCount > 0 ? (
//                                // match size of other icons for consistent alignment
//                                <SailboatIcon size={33} />
//                              ) : (
//                                <i className="bi bi-plus-square-fill fs-3"></i>
//                              )}
//                              <span>{user?.vesselsCount > 0 ? 'My vessels' : 'Add vessel'}</span>
//                            </div>
//             )}

//             {/* Post for role 2, 3, 4 */}
//             {/* {(isRole2 || isRole3or4) && (
//               <div
//                 className="d-flex flex-column align-items-center text-secondary small"
//                 onClick={handlePostClick}
//               >
//                 <i className="bi bi-plus-square-fill fs-3"></i>
//                 <span>{t('mobileFooter.post', { defaultValue: 'Post' })}</span>
//               </div>
//             )} */}

//               <div
//               className="d-flex flex-column align-items-center text-secondary small"
//               onClick={() => navigate("/mates")}
//             >
//               <i className="bi bi-people-fill fs-3"></i>
//               {/* <MdGroups size={35} /> */}
//               <span>{t('leftPanel.nav.mates', { defaultValue: 'Mates' })}</span>
//             </div>

//             {/* Sail board for role 2, Job for role 3/4 */}
//             {showQuotes && isRole2 && (
//               <div
//                 className="d-flex flex-column align-items-center text-secondary small"
//                 onClick={() => navigate("/delivery-qoutes")}
//               >
//                 <i className="bi bi-briefcase-fill fs-3"></i>
//                 <span>{t('mobileFooter.quotes', { defaultValue: 'Quotes' })}</span>
//               </div>
//             )}

//             {isRole3or4 && (
//               <div
//                 className="d-flex flex-column align-items-center text-secondary small"
//                 onClick={() => navigate("/delivery-qoutes")}
//               >
//                 <i className="bi bi-briefcase-fill fs-3"></i>
//                 <span>Jobs</span>
//               </div>
//             )}
//             {/* Alert: role 2 (before sail board), role 3/4 (last) */}
//             {(isRole2 || isRole3or4) && (
//               <div ref={bellRef} className="d-flex flex-column align-items-center text-secondary small position-relative" onClick={() => {
//                 // Use a modal fallback on narrow/mobile screens since Overlay positioning can be unreliable
//                 // if (typeof window !== 'undefined' && window.innerWidth <= 800) {
//                 setShowFallbackModal(true);
//                 // } else {
//                 //   setNotifTarget(bellRef.current);
//                 //   setShowNotifPop(true);
//                 // }
//               }}>
//                 <i className="bi bi-bell-fill fs-3"></i>
//                 {unreadCount > 0 && (
//                   <span className="position-absolute top-0 start-50 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.6rem' }}>{unreadCount}</span>
//                 )}
//                 <span>{t('mobileFooter.alert', { defaultValue: 'Alert' })}</span>
//               </div>
//             )}
//           </div>
//           {/* Notification popover for mobile footer */}
//           <NotificationPop
//             show={showNotifPop}
//             target={notifTarget}
//             setShow={setShowNotifPop}
//             liveNotification={liveNotification}
//             setLiveNotification={setLiveNotification}
//           />
//           {/* Fallback modal shown on mobile if Overlay-based NotificationPop fails/doesn't open */}
//           <Modal show={showFallbackModal} onHide={() => setShowFallbackModal(false)} centered>
//             <Modal.Header closeButton>
//               <Modal.Title>{t('mobileFooter.notifications', { defaultValue: 'Notifications' })}</Modal.Title>
//             </Modal.Header>
//             <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
//               {loadingFallback ? (
//                 <div className="text-center py-3">{t('common.loading', { defaultValue: 'Loading...' })}</div>
//               ) : notificationsFallback && notificationsFallback.length > 0 ? (
//                 notificationsFallback.map((notif) => (
//                   <div className="mb-3" key={notif.id}>
//                     <div className="d-flex justify-content-between align-items-start">
//                       <div>
//                         <p className="mb-1 fw-bold">{notif.title || t('mobileFooter.notification_default', { defaultValue: 'Notification' })}</p>
//                         <p className="mb-1 text-muted small">{notif.message}</p>
//                         <div className="d-flex gap-2 mt-2">
//                           {(notif.link || notif.type === "friend_request") && (
//                             <button
//                               className="btn btn-blue btn-sm"
//                               onClick={() => {
//                                 // Close modal first
//                                 setShowFallbackModal(false);
//                                 // If friend request, navigate to personal-information with personId in state
//                                 if (notif.type === "friend_request") {
//                                   const senderId = notif.sender_id || notif.senderId || (notif.sender && (notif.sender.id || notif.sender_id)) || notif.sender?.id;
//                                   navigate("/personal-information", { state: { personId: senderId } });
//                                   return;
//                                 }

//                                 // If this is a group post, navigate to notif.link and pass groupId in state
//                                 if (notif.type === "group_event"  && notif.link) {
//                                   console.log(notif," navigating to group post");
//                                   const groupId = notif.sender_id || notif.senderId || (notif.sender && (notif.sender.id || notif.sender_id)) || notif.sender?.id;
//                                   try {
//                                     // If notif.link is an absolute URL to our frontend, convert to pathname so react-router can navigate and receive state
//                                     const urlObj = new URL(notif.link);
//                                     const isSameOrigin = urlObj.origin === window.location.origin;
//                                     const path = `${urlObj.pathname || ''}${urlObj.search || ''}${urlObj.hash || ''}`;
//                                     if (isSameOrigin) {
//                                       navigate(path, { state: { groupId } });
//                                     } else {
//                                       // external URL — do a full redirect and append groupId as query param (optional)
//                                       const sep = notif.link.includes('?') ? '&' : '?';
//                                       window.location.href = `${notif.link}${sep}groupId=${encodeURIComponent(groupId)}`;
//                                     }
//                                   } catch (err) {
//                                     // notif.link is probably a relative path — navigate directly
//                                     navigate(notif.link, { state: { groupId } });
//                                   }
//                                   return;
//                                 }

//                                 // Otherwise follow link (external or internal)
//                                 if (notif.link && /^https?:\/\//.test(notif.link)) {
//                                   window.location.href = notif.link;
//                                 } else if (notif.link) {
//                                   navigate(notif.link);
//                                 }
//                               }}
//                             >
//                               {t('mobileFooter.view_details', { defaultValue: 'View Details' })}
//                             </button>
//                           )}
//                           {!notif.is_read && (
//                             <button className="btn btn-outline-success btn-sm" onClick={async () => { await markAsReadFallback(notif.id); }}>
//                               {t('mobileFooter.mark_as_read', { defaultValue: 'Mark as Read' })}
//                             </button>
//                           )}
//                         </div>
//                       </div>
//                       <div className="text-end small text-muted">
//                         {notif.hour_ago}
//                       </div>
//                     </div>
//                     <hr />
//                   </div>
//                 ))
//               ) : (
//                 <div className="text-center text-muted">{t('mobileFooter.no_notifications', { defaultValue: 'No notifications found.' })}</div>
//               )}
//             </Modal.Body>
//           </Modal>
//           {showCreatePostModal && (
//             <div style={{ position: 'fixed', zIndex: 9999, inset: 0 }}>
//               {/* Lazy load CreatePostModal to avoid import issues if not present */}
//               {(() => {
//                 const CreatePostModal = React.lazy(() => import('../Posts/CreatePostModal'));
//                 return (
//                   <React.Suspense fallback={<div>Loading...</div>}>
//                     <CreatePostModal
//                       onClose={() => setShowCreatePostModal(false)}
//                       url={process.env.REACT_APP_LARAVEL_DOMAIN_URL}
//                       ispublic={user?.is_public}
//                       onPostSuccess={() => setShowCreatePostModal(false)}
//                       onPostError={() => setShowCreatePostModal(false)}
//                     />
//                   </React.Suspense>
//                 );
//               })()}
//             </div>
//           )}
//         </>
//       )}
//     </>
//   );
// }

// export default MobileFooter;
